import { useCallback, useEffect, useState } from "react";
import { useTran } from "@/hooks";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FrameworkBadge } from "@/components/common";
import { PATHS } from "@/lib/router";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  RefreshCcw,
  BarChart2,
  Target,
  CheckCircle2,
  ChartNoAxesColumn,
  CalendarRange,
  Layers,
  ArrowRight,
} from "lucide-react";
import {
  masterDataService,
  reportPreviewService,
} from "@/features/esg/services";
import { reportingBoundaryService } from "@/features/reporting-boundary/services/reporting-boundary.services";
import { useAuthStore } from "@/features/auth/hooks";

const StatsTile = ({ icon: Icon, title, children }) => (
  <div className="flex min-w-0 items-center justify-between gap-3 rounded-radius border border-border p-4">
    <div className="flex min-w-0 items-center gap-2 text-sm font-medium text-muted-foreground">
      {Icon && <Icon size={16} className="shrink-0" />}
      <span>{title}</span>
    </div>
    <div className="shrink-0 text-right text-sm text-foreground">
      {children}
    </div>
  </div>
);

const formatReportingPeriod = (reportingYears = []) => {
  if (!Array.isArray(reportingYears) || reportingYears.length === 0) {
    return "";
  }

  const periods = reportingYears
    .map((year) => ({
      startYear: Number.parseInt(year?.startYear, 10),
      startMonth: Number.parseInt(year?.startMonth, 10),
      endYear: Number.parseInt(year?.endYear, 10),
      endMonth: Number.parseInt(year?.endMonth, 10),
    }))
    .filter(
      (period) =>
        Number.isFinite(period.startYear) &&
        Number.isFinite(period.startMonth) &&
        Number.isFinite(period.endYear) &&
        Number.isFinite(period.endMonth),
    );

  if (periods.length === 0) {
    return "";
  }

  const minPeriod = periods.reduce((min, current) => {
    const currentDate = current.startYear * 100 + current.startMonth;
    const minDate = min.startYear * 100 + min.startMonth;

    return currentDate < minDate ? current : min;
  });

  const maxPeriod = periods.reduce((max, current) => {
    const currentDate = current.endYear * 100 + current.endMonth;
    const maxDate = max.endYear * 100 + max.endMonth;

    return currentDate > maxDate ? current : max;
  });

  const start = `${String(minPeriod.startMonth).padStart(2, "0")}/${minPeriod.startYear}`;
  const end = `${String(maxPeriod.endMonth).padStart(2, "0")}/${maxPeriod.endYear}`;

  return `${start} - ${end}`;
};

const actionButtons = [
  // {
  //   key: "esgFramework",
  //   icon: FileText,
  //   path: PATHS.ESG_FRAMEWORKS,
  // },
  {
    key: "kpiDashboard",
    icon: BarChart2,
    path: PATHS.ANALYTICS_DASHBOARD,
  },
  // {
  //   key: "kpiTargets",
  //   icon: Target,
  //   path: PATHS.ESG_TARGETS,
  // },
  {
    key: "esgReports",
    icon: CheckCircle2,
    path: PATHS.ESG_REPORT_PREVIEW_LIST,
  },
];

const ESGOverviewCard = ({ company, loading, onRefresh }) => {
  const { t } = useTran();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [esgLoading, setEsgLoading] = useState(false);
  const [esgStats, setEsgStats] = useState({
    totalReports: 0,
    frameworks: [],
    targetMetrics: 0,
    reportingPeriod: "",
  });

  const loadEsgStats = useCallback(async () => {
    setEsgLoading(true);
    try {
      const companyId =
        user?.companyId ||
        user?.company?._id ||
        user?.company?.id ||
        user?.orgId;
      const assignmentPromise = companyId
        ? masterDataService.list({ companyId }, { throwOnError: false })
        : Promise.resolve([]);

      const [
        previewReportsData,
        reportingYears,
        frameworkData,
        assignmentData,
      ] = await Promise.all([
        reportPreviewService.listReports({}, { throwOnError: false }),
        reportingBoundaryService.getReportingYearByCompany(),
        reportingBoundaryService.getFrameworks(),
        assignmentPromise,
      ]);
      const previewReports = Array.isArray(previewReportsData?.items)
        ? previewReportsData.items
        : Array.isArray(previewReportsData?.reports)
          ? previewReportsData.reports
          : Array.isArray(previewReportsData)
            ? previewReportsData
            : [];

      const frameworkNameMap = new Map(
        (frameworkData?.frameworks || []).map((framework) => [
          String(framework?.code || "").trim(),
          framework?.name || framework?.code,
        ]),
      );

      const years = reportingYears?.reportingYears || [];

      const selectedFrameworkCodes = [
        ...new Set(
          years.flatMap((reportingYear) => reportingYear?.frameworkCodes ?? []),
        ),
      ];

      const selectedFrameworks = selectedFrameworkCodes
        .map((code) => {
          const normalizedCode = String(code || "").trim();
          if (!normalizedCode) return null;
          return frameworkNameMap.get(normalizedCode) || normalizedCode;
        })
        .filter(Boolean);

      const reportingPeriod = formatReportingPeriod(years);
      const targetMetrics = Array.isArray(assignmentData)
        ? assignmentData.length
        : 0;

      setEsgStats({
        totalReports: previewReports.length,
        frameworks: selectedFrameworks,
        targetMetrics,
        reportingPeriod,
      });
    } catch (error) {
      console.error("Failed to load ESG overview stats:", error);
    } finally {
      setEsgLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadEsgStats();
  }, [loadEsgStats]);

  const { totalReports, frameworks, targetMetrics, reportingPeriod } = esgStats;

  return (
    <Card className="h-fit self-start rounded-radius pt-0">
      <CardHeader className="rounded-t-lg grid gap-2 py-3 bg-gradient-to-br from-slate-100 to-white dark:from-white/10 dark:to-white/5">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="flex min-w-0 items-center gap-2 text-base text-primary">
            <ChartNoAxesColumn size={20} />
            {t("companyInformation.esgOverview")}
          </CardTitle>
          <Button
            size="xs"
            variant="ghost"
            className="shrink-0 gap-2 text-secondary hover:text-primary dark:text-primary dark:hover:text-secondary"
            onClick={() => {
              onRefresh?.();
              loadEsgStats();
            }}
            disabled={!company}
            type="button"
          >
            <RefreshCcw />
            {t("Refresh")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading || esgLoading ? (
          <p className="text-xs text-muted-foreground">
            {t("Syncing latest company details...")}
          </p>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <StatsTile icon={FileText} title={t("Created Reports")}>
            {totalReports}
          </StatsTile>
          <StatsTile icon={Target} title={t("Assigned Metrics")}>
            {targetMetrics}
          </StatsTile>
          <StatsTile icon={CalendarRange} title={t("Reporting Period")}>
            {reportingPeriod || t("Not set")}
          </StatsTile>
          <StatsTile icon={Layers} title={t("Active Frameworks")}>
            {frameworks.length}
          </StatsTile>
        </div>
        <div className="py-3">
          <div className="flex items-center gap-2 text-sm font-medium text-sky-600">
            <CheckCircle2 size={16} />
            <span>{t("Selected Frameworks")}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {frameworks.length > 0 ? (
              frameworks.map((name) => (
                <FrameworkBadge key={name} framework={name} />
              ))
            ) : (
              <span className="text-xs text-muted-foreground">
                {t("Not set")}
              </span>
            )}
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-2">
          {actionButtons.map(({ key, icon: Icon, path }) => (
            <Button
              key={key}
              size="lg"
              variant="outline"
              className="h-auto justify-between gap-2 whitespace-normal py-3 text-left text-primary"
              onClick={() => navigate(path)}
            >
              <span className="flex items-center gap-2">
                {Icon ? <Icon className="h-4 w-4" /> : null}
                {t(`companyInformation.${key}`)}
              </span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default ESGOverviewCard;
